import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { GrowthCrmService, GrowthLeadResponse } from './growth-crm.service';

@Component({
  standalone: true,
  selector: 'ain-growth-leads',
  imports: [
    AinPageComponent, AinStateComponent, AinFormFieldComponent, InfoHintComponent,
    FormsModule, TableModule, ButtonModule, SelectModule, DialogModule, InputTextModule,
    PartySelectorComponent,
    RouterLink
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.crm.leads.title')"
      [description]="t('growth.crm.leads.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.crm.leads.help.page.title'"
        [descriptionKey]="'growth.crm.leads.help.page.description'"
        [bulletKeys]="[
          'growth.crm.leads.help.page.bullets.partyRef',
          'growth.crm.leads.help.page.bullets.scoring',
          'growth.crm.leads.help.page.bullets.handoff'
        ]" />

      <div class="toolbar">
        <div class="toolbar-start">
          <button pButton type="button" severity="secondary" icon="pi pi-refresh" [label]="t('common.refresh')" (click)="load()"></button>
        </div>
        <div class="toolbar-end">
          <a pButton [routerLink]="['/growth/crm/leads/new']" [label]="t('growth.crm.leads.create')"></a>
        </div>
      </div>

      <section class="card">
        <div class="section-title">
          <h3>{{ t('growth.crm.leads.sections.filters') }}</h3>
          <ain-info-hint
            [titleKey]="'growth.crm.leads.help.filters.title'"
            [descriptionKey]="'growth.crm.leads.help.filters.description'"
            [bulletKeys]="[
              'growth.crm.leads.help.filters.bullets.status',
              'growth.crm.leads.help.filters.bullets.aceRefs'
            ]" />
        </div>
        <div class="filter-grid">
          <ain-form-field [label]="t('growth.crm.leads.filters.status')">
            <p-select
              [options]="statusOptions"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="filterStatus"
              [showClear]="true"
              [placeholder]="t('growth.crm.leads.filters.status')"
              appendTo="body" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.crm.leads.filters.source')">
            <p-select
              [options]="sourceOptions"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="filterSource"
              [showClear]="true"
              [placeholder]="t('growth.crm.leads.filters.source')"
              appendTo="body" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.crm.leads.filters.channel')">
            <p-select
              [options]="channelOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="filterChannel"
              [showClear]="true"
              [placeholder]="t('growth.crm.leads.filters.channel')"
              appendTo="body" />
          </ain-form-field>
        </div>
        <div class="filter-actions">
            <button pButton type="button" [label]="t('catalog.action.search')" (click)="load()"></button>
            <button pButton type="button" severity="secondary" [label]="t('growth.crm.actions.clearFilters')" (click)="clearFilters()"></button>
        </div>
      </section>

      <section class="card">
        <div class="section-title">
          <h3>{{ t('growth.crm.leads.sections.list') }}</h3>
        </div>
        @if (loading()) {
          <ain-state type="loading" [title]="t('growth.crm.leads.loading')" />
        } @else if (error()) {
          <ain-state type="error" [title]="t('growth.crm.leads.errorTitle')" [message]="error() ?? ''" />
        } @else if (leads().length === 0) {
          <ain-state type="empty" [title]="t('growth.crm.leads.empty')" />
        } @else {
          <p-table [value]="leads()" [paginator]="true" [rows]="15">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.crm.leads.columns.leadNumber') }}</th>
                <th>{{ t('growth.crm.leads.columns.status') }}</th>
                <th>{{ t('growth.crm.leads.columns.source') }}</th>
                <th>{{ t('growth.crm.leads.columns.channel') }}</th>
                <th>{{ t('growth.crm.leads.columns.score') }}</th>
                <th>{{ t('growth.crm.leads.columns.contentAsset') }}</th>
                <th>{{ t('growth.crm.leads.columns.actions') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.leadNumber }}</td>
                <td>{{ localizeStatus(row.status) }}</td>
                <td>{{ localizeSource(row.source) }}</td>
                <td>{{ channelLabel(row.channelKey) }}</td>
                <td>{{ row.score ?? '-' }}</td>
                <td>{{ row.contentAssetId ? t('growth.crm.leads.refs.linked') : '-' }}</td>
                <td class="actions">
                  <a pButton size="small" [routerLink]="['/growth/crm/leads', row.id]" [label]="t('growth.crm.leads.actions.detail')"></a>
                </td>
              </tr>
            </ng-template>
          </p-table>
        }
      </section>

      <p-dialog
        [header]="t('growth.crm.leads.create')"
        [visible]="showCreateDialog()"
        (visibleChange)="showCreateDialog.set($event)"
        [modal]="true"
        [style]="{ width: 'min(560px, 92vw)' }">
        <div class="dialog-section">
          <div class="section-title">
            <h4>{{ t('growth.crm.leads.sections.createCore') }}</h4>
            <ain-info-hint
              [titleKey]="'growth.crm.leads.help.create.title'"
              [descriptionKey]="'growth.crm.leads.help.create.description'"
              [bulletKeys]="['growth.crm.leads.help.create.bullets.party']" />
          </div>
          <div class="dialog-grid">
            <ain-form-field [label]="t('growth.crm.leads.fields.party')">
              <ain-party-selector [(ngModel)]="createModel.partyId" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.crm.leads.fields.source')">
              <p-select [options]="sourceOptions" optionLabel="label" optionValue="value" [(ngModel)]="createModel.source" appendTo="body" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.crm.leads.fields.channel')">
              <p-select [options]="channelOptions()" optionLabel="label" optionValue="value" [(ngModel)]="createModel.channelKey" appendTo="body" />
            </ain-form-field>
          </div>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" [label]="t('common.cancel')" severity="secondary" (click)="showCreateDialog.set(false)"></button>
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="createLead()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog
        [header]="t('growth.crm.leads.detailTitle')"
        [visible]="showDetailDialog()"
        (visibleChange)="showDetailDialog.set($event)"
        [modal]="true"
        [style]="{ width: 'min(720px, 92vw)' }">
        @if (selectedLead()) {
          <section class="card dialog-card">
            <div class="section-title">
              <h4>{{ t('growth.crm.leads.sections.detailOverview') }}</h4>
              <ain-info-hint
                [titleKey]="'growth.crm.leads.help.detail.title'"
                [descriptionKey]="'growth.crm.leads.help.detail.description'"
                [bulletKeys]="[
                  'growth.crm.leads.help.detail.bullets.score',
                  'growth.crm.leads.help.detail.bullets.handoff'
                ]" />
            </div>
            <div class="detail-grid">
              <p><strong>{{ t('growth.crm.leads.columns.leadNumber') }}:</strong> {{ selectedLead()!.leadNumber }}</p>
              <p><strong>{{ t('growth.crm.leads.columns.status') }}:</strong> {{ localizeStatus(selectedLead()!.status) }}</p>
              <p><strong>{{ t('growth.crm.leads.columns.score') }}:</strong> {{ selectedLead()!.score ?? '-' }}</p>
              <p><strong>{{ t('growth.crm.leads.fields.contentAsset') }}:</strong> {{ selectedLead()!.contentAssetId ? t('growth.crm.leads.refs.linked') : '-' }}</p>
              <p><strong>{{ t('growth.crm.leads.fields.campaign') }}:</strong> {{ selectedLead()!.campaignId ? t('growth.crm.leads.refs.linked') : '-' }}</p>
              <p><strong>{{ t('growth.crm.leads.fields.engagementEvent') }}:</strong> {{ selectedLead()!.engagementEventId ? t('growth.crm.leads.refs.linked') : '-' }}</p>
            </div>
          </section>
          <div class="detail-actions">
            <button pButton type="button" [label]="t('growth.crm.leads.actions.score')" (click)="scoreSelected()"></button>
            <button pButton type="button" [label]="t('growth.crm.leads.actions.qualify')" (click)="qualifySelected()"></button>
            <button pButton type="button" severity="danger" [label]="t('growth.crm.leads.actions.disqualify')" (click)="disqualifySelected()"></button>
            <button pButton type="button" [label]="t('growth.crm.leads.actions.handoff')" (click)="handoffSelected()"></button>
          </div>
        }
        <ng-template pTemplate="footer">
          <div class="dialog-footer">
            <button pButton type="button" severity="secondary" [label]="t('common.close')" (click)="showDetailDialog.set(false)"></button>
          </div>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap; }
    .toolbar-start, .toolbar-end, .filter-actions, .actions, .detail-actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 10px; }
    .card h3, .card h4 { margin: 0; }
    .dialog-card { margin: 0; }
    .section-title { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .filter-grid, .dialog-grid, .detail-grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
    .detail-grid p { margin: 0; }
    .dialog-section { display: grid; gap: 10px; }
    .dialog-footer { display: flex; justify-content: flex-end; gap: 8px; flex-wrap: wrap; }
  `]
})
export class GrowthLeadsComponent implements OnInit {
  private readonly crmService = inject(GrowthCrmService);
  private readonly localizationService = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly leads = signal<GrowthLeadResponse[]>([]);
  readonly showCreateDialog = signal(false);
  readonly showDetailDialog = signal(false);
  readonly selectedLead = signal<GrowthLeadResponse | null>(null);

  filterStatus: string | null = null;
  filterSource: string | null = null;
  filterChannel: string | null = null;

  createModel = {
    partyId: null as string | null,
    source: 'form_submit',
    channelKey: 'web'
  };

  readonly statusOptions = [
    { value: 'New', labelKey: 'growth.crm.leads.status.new' },
    { value: 'Scored', labelKey: 'growth.crm.leads.status.scored' },
    { value: 'Qualified', labelKey: 'growth.crm.leads.status.qualified' },
    { value: 'HandedOff', labelKey: 'growth.crm.leads.status.handedOff' },
    { value: 'Disqualified', labelKey: 'growth.crm.leads.status.disqualified' },
    { value: 'Merged', labelKey: 'growth.crm.leads.status.merged' }
  ];

  readonly sourceOptions = [
    { value: 'form_submit', labelKey: 'growth.crm.leads.source.formSubmit' },
    { value: 'request_demo', labelKey: 'growth.crm.leads.source.requestDemo' },
    { value: 'request_quote', labelKey: 'growth.crm.leads.source.requestQuote' },
    { value: 'social_comment', labelKey: 'growth.crm.leads.source.socialComment' },
    { value: 'social_dm', labelKey: 'growth.crm.leads.source.socialDm' },
    { value: 'manual', labelKey: 'growth.crm.leads.source.manual' }
  ];

  channelOptions(): Array<{ value: string; label: string }> {
    return [
      { value: 'web', label: this.t('growth.crm.leads.channel.web') },
      { value: 'email', label: this.t('growth.crm.leads.channel.email') },
      { value: 'social', label: this.t('growth.crm.leads.channel.social') },
      { value: 'landing', label: this.t('growth.crm.leads.channel.landing') }
    ];
  }

  channelLabel(channelKey: string): string {
    return this.channelOptions().find(item => item.value === channelKey)?.label ?? channelKey;
  }

  ngOnInit(): void {
    this.localizeOptions();
    this.load();
  }

  private localizeOptions(): void {
    for (const group of [this.statusOptions, this.sourceOptions]) {
      for (const item of group) {
        (item as { label?: string }).label = this.t(item.labelKey);
      }
    }
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.crmService.listLeads({
      status: this.filterStatus ?? undefined,
      source: this.filterSource ?? undefined,
      channelKey: this.filterChannel ?? undefined
    }).subscribe({
      next: items => {
        this.leads.set(items);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('growth.crm.leads.errorLoad'));
        this.loading.set(false);
      }
    });
  }

  clearFilters(): void {
    this.filterStatus = null;
    this.filterSource = null;
    this.filterChannel = null;
    this.load();
  }

  openCreateDialog(): void {
    this.createModel = { partyId: null, source: 'form_submit', channelKey: 'web' };
    this.showCreateDialog.set(true);
  }

  createLead(): void {
    this.saving.set(true);
    this.crmService.createLead({
      source: this.createModel.source,
      channelKey: this.createModel.channelKey,
      partyId: this.createModel.partyId
    }).subscribe({
      next: () => {
        this.showCreateDialog.set(false);
        this.saving.set(false);
        this.load();
      },
      error: () => {
        this.error.set(this.t('growth.crm.leads.errorCreate'));
        this.saving.set(false);
      }
    });
  }

  openDetail(row: GrowthLeadResponse): void {
    this.selectedLead.set(row);
    this.showDetailDialog.set(true);
  }

  scoreSelected(): void {
    const lead = this.selectedLead();
    if (!lead) return;
    this.crmService.scoreLead(lead.id, 72, '{"mode":"advisory","reason":"manual-admin-score"}').subscribe({
      next: updated => {
        this.selectedLead.set(updated);
        this.load();
      }
    });
  }

  qualifySelected(): void {
    const lead = this.selectedLead();
    if (!lead) return;
    this.crmService.qualifyLead(lead.id).subscribe({
      next: updated => {
        this.selectedLead.set(updated);
        this.load();
      }
    });
  }

  disqualifySelected(): void {
    const lead = this.selectedLead();
    if (!lead) return;
    this.crmService.disqualifyLead(lead.id).subscribe({
      next: updated => {
        this.selectedLead.set(updated);
        this.load();
      }
    });
  }

  handoffSelected(): void {
    const lead = this.selectedLead();
    if (!lead) return;
    this.crmService.handoffLead(lead.id, 1).subscribe({
      next: result => {
        this.selectedLead.set(result.lead);
        this.load();
      }
    });
  }

  localizeStatus(status: string): string {
    const key = `growth.crm.leads.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
    const translated = this.t(key);
    return translated === key ? status : translated;
  }

  localizeSource(source: string): string {
    const map: Record<string, string> = {
      form_submit: 'growth.crm.leads.source.formSubmit',
      request_demo: 'growth.crm.leads.source.requestDemo',
      request_quote: 'growth.crm.leads.source.requestQuote',
      social_comment: 'growth.crm.leads.source.socialComment',
      social_dm: 'growth.crm.leads.source.socialDm',
      manual: 'growth.crm.leads.source.manual'
    };
    return map[source] ? this.t(map[source]) : source;
  }

  t(key: string): string {
    return this.localizationService.translate(key);
  }
}
